import { StepLayout } from '@/components/NewGeneralDialog/StepLayout';
import { Documents } from '@/types/company';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Col } from '@treyd-io/treyd-ui/components/Grid/Col';
import { Row } from '@treyd-io/treyd-ui/components/Grid/Row';
import ArrowRightIcon from '@treyd-io/treyd-ui/icons/ArrowRightIcon';
import { getYear, subYears } from 'date-fns';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import * as Yup from 'yup';

interface IncomeInformationProps {
  formData: Partial<Documents.DocumentType['data']>;
  currency: string;
  onMovingForward: (values: Partial<Documents.DocumentType['data']>) => void;
  onMovingBackward: () => void;
}

export const IncomeInformation = ({
  formData,
  currency,
  onMovingForward,
  onMovingBackward,
}: IncomeInformationProps) => {
  const { t } = useTranslation();

  const incomeCurrency = formData?.Income_Currency || currency;
  const lastYear = getYear(subYears(new Date(), 1));

  const initialValues = {
    income_year: formData?.Income_Year || lastYear,
    income_amount: formData?.Income_Amount || '',
  };

  const validationSchema = Yup.object({
    income_year: Yup.number()
      .typeError('Enter a valid year')
      .min(1900)
      .max(new Date().getFullYear())
      .required('Year is required'),
    income_amount: Yup.string().required('Required'),
  });

  const handleSubmit = (values: typeof initialValues) => {
    onMovingForward({
      Income_Currency: incomeCurrency,
      Income_Year: values.income_year,
      Income_Amount: values.income_amount,
    });
  };

  return (
    <Box maxWidth={960} margin="auto" marginBottom={6}>
      <StepLayout
        onMovingBackward={onMovingBackward}
        containerProps={{
          gap: 1,
        }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount>
          {(formik) => (
            <Form>
              <Stack width="100%" gap={3}>
                <Box>
                  <Typography
                    variant="h4"
                    color="text.primary"
                    marginBottom={1}>
                    {t('income_information')}
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {t('please_provide_your_income_details_below')}
                  </Typography>
                </Box>

                <Row rowSpacing={3}>
                  <Col xs={12} sm={6}>
                    <DatePicker
                      label={t('income_year')}
                      views={['year']}
                      openTo="year"
                      disableFuture={true}
                      format="yyyy"
                      name="income_year"
                      value={
                        formik.values.income_year
                          ? new Date(Number(formik.values.income_year), 0)
                          : null
                      }
                      onChange={(date) => {
                        const year = date ? date.getFullYear().toString() : '';
                        formik.setFieldValue('income_year', year);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error:
                            formik.touched.income_year &&
                            !!formik.errors.income_year,
                          helperText:
                            formik.touched.income_year &&
                            formik.errors.income_year,
                          onBlur: formik.handleBlur,
                        },
                      }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <NumericFormat
                      fullWidth
                      customInput={TextField}
                      label={t('annual_income_before_tax_last_year')}
                      name="income_amount"
                      type="text"
                      value={formik.values.income_amount}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.income_amount &&
                        !!formik.errors.income_amount
                      }
                      slotProps={{
                        input: {
                          endAdornment: (
                            <Typography variant="body2" color="text.disabled">
                              {incomeCurrency}
                            </Typography>
                          ),
                        },
                        htmlInput: {
                          min: 0,
                        },
                      }}
                      helperText={
                        formik.touched.income_amount &&
                        formik.errors.income_amount
                      }
                      thousandSeparator={' '}
                      decimalSeparator=","
                      allowedDecimalSeparators={[',', '.']}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Col>
                </Row>

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  endIcon={<ArrowRightIcon />}
                  disabled={!formik.isValid}
                  sx={{ width: { sm: 'fit-content' }, marginTop: 0.5 }}>
                  {t('continue')}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </StepLayout>
    </Box>
  );
};
